import { z } from "zod";
import { createAttribute } from "@coltorapps/builder";

import type { Operator } from "@/types/flow";
import type {
  ColumnType,
  ExternalApiRequestConfig,
} from "@/types/master-data-dataset";

const modalDatasourceFilterModeSchema = z.enum(["manual", "code"]);

const modalDatasourceFilterSchema = z.object({
  columnKey: z.string(),
  operator: z.enum([
    ">",
    "<",
    "==",
    "!=",
    ">=",
    "<=",
    "contains",
    "not_contains",
    "equals",
    "not_equals",
  ]),
  value: z.union([z.string(), z.number()]),
});

const externalApiRequestConfigSchema = z.object({
  url: z.string(),
  method: z.enum(["GET", "POST", "PUT"]),
  headers: z.record(z.string(), z.string()).optional(),
  body: z.string().optional(),
});

const columnTypeSchema = z.enum(["text", "boolean", "number", "date"]);

const modalDatasourceExternalColumnSchema = z.object({
  columnKey: z.string(),
  displayName: z.string(),
  type: columnTypeSchema,
  formField: z.string(),
});

const modalDatasourceMasterDataColumnSchema = z.object({
  columnKey: z.string(),
  displayName: z.string(),
  formField: z.string().optional(),
});

const modalDatasourceExternalConfigSchema = z.object({
  api_config: externalApiRequestConfigSchema,
  field_mappings: z.object({
    records_path: z.string(),
    mappings: z.array(modalDatasourceExternalColumnSchema),
  }),
  filters: z.array(modalDatasourceFilterSchema),
  filterMode: modalDatasourceFilterModeSchema.optional(),
  filterExpression: z.string().optional(),
});

const modalDatasourceMasterDataConfigSchema = z.object({
  tableCode: z.string(),
  columns: z.array(modalDatasourceMasterDataColumnSchema),
  filters: z.array(modalDatasourceFilterSchema),
  filterMode: modalDatasourceFilterModeSchema.optional(),
  filterExpression: z.string().optional(),
});

export type ModalDatasourceFilterMode = "manual" | "code";

const modalDatasourceSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("external-api"),
    config: modalDatasourceExternalConfigSchema,
  }),
  z.object({
    type: z.literal("master-data"),
    config: modalDatasourceMasterDataConfigSchema,
  }),
]);

export type ModalDatasourceFilter = {
  columnKey: string;
  operator: Operator;
  value: string | number;
};

export type ModalDatasourceExternalColumn = {
  columnKey: string;
  displayName: string;
  type: ColumnType;
  formField: string;
};

export type ModalDatasourceMasterDataColumn = {
  columnKey: string;
  displayName: string;
  formField?: string;
};

export type ModalDatasourceExternalConfig = {
  api_config: ExternalApiRequestConfig;
  field_mappings: {
    records_path: string;
    mappings: ModalDatasourceExternalColumn[];
  };
  filters: ModalDatasourceFilter[];
  filterMode?: ModalDatasourceFilterMode;
  filterExpression?: string;
};

export type ModalDatasourceMasterDataConfig = {
  tableCode: string;
  columns: ModalDatasourceMasterDataColumn[];
  filters: ModalDatasourceFilter[];
  filterMode?: ModalDatasourceFilterMode;
  filterExpression?: string;
};

export type ModalDatasourceValue =
  | {
      type: "external-api";
      config: ModalDatasourceExternalConfig;
    }
  | {
      type: "master-data";
      config: ModalDatasourceMasterDataConfig;
    };

export const createDefaultModalDatasourceValue = (
  type: ModalDatasourceValue["type"] = "master-data",
): ModalDatasourceValue =>
  type === "external-api"
    ? {
        type: "external-api",
        config: {
          api_config: {
            url: "",
            method: "GET",
          },
          field_mappings: {
            records_path: "",
            mappings: [],
          },
          filters: [],
          filterMode: "manual",
          filterExpression: undefined,
        },
      }
    : {
        type: "master-data",
        config: {
          tableCode: "",
          columns: [],
          filters: [],
          filterMode: "manual",
          filterExpression: undefined,
        },
      };

export const modalDatasourceAttribute = createAttribute({
  name: "modalDatasource",
  validate(value) {
    return modalDatasourceSchema
      .optional()
      .catch(createDefaultModalDatasourceValue())
      .parse(value);
  },
});
